import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { forgotPassword, verifyOTP, resetPassword } from '../services/api';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [resetToken, setResetToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    // Clear session details on mount to guarantee fresh states
    useEffect(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }, []);

    // OTP Input helpers matching visual standards of 2FA verification
    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;
        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);
        if (element.value && element.nextElementSibling) {
            element.nextElementSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            if (!otp[index] && e.target.previousElementSibling) {
                e.target.previousElementSibling.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            const newOtp = [...otp];
            pastedData.split('').forEach((char, i) => {
                if (i < 6) newOtp[i] = char;
            });
            setOtp(newOtp);
            const nextIndex = Math.min(pastedData.length, 5);
            const inputs = document.querySelectorAll('input[name="otp"]');
            if (inputs[nextIndex]) {
                inputs[nextIndex].focus();
            }
        }
    };

    // Form handlers
    const handleRequestOtp = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error("Please enter your email address.");
            return;
        }
        setLoading(true);
        try {
            const response = await forgotPassword(email);
            toast.success(response.data.message || "A secure verification code has been dispatched!");
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.message || "Email address not found or recovery disabled.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpString = otp.join("");
        if (otpString.length !== 6) {
            toast.error("Please enter the complete 6-digit code.");
            return;
        }
        setLoading(true);
        try {
            const response = await verifyOTP(email, otpString);
            toast.success("Security code verified successfully.");
            setResetToken(response.data.reset_token);
            setStep(3);
        } catch (err) {
            toast.error(err.response?.data?.message || "Invalid or expired verification code.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters long.");
            return;
        }
        if (password !== confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const response = await resetPassword({
                email,
                reset_token: resetToken,
                password,
                password_confirmation: confirmPassword
            });
            toast.success(response.data.message || "Password successfully changed!");
            setTimeout(() => {
                navigate('/login');
            }, 1500);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to reset password. Please request a new OTP.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="auth-page">
            <div className="auth-card">
                <div className="icon-container">🔑</div>
                
                {step === 1 && (
                    <>
                        <div className="auth-header">
                            <h3>Forgot Password?</h3>
                            <p className="text-muted">Enter your email below to request a secure verification code.</p>
                        </div>
                        <form onSubmit={handleRequestOtp}>
                            <div className="mb-3 text-start">
                                <label className="form-label">Email Address</label>
                                <input 
                                    type="email" 
                                    className="form-control" 
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)} 
                                    placeholder="your-email@example.com"
                                    required 
                                />
                            </div>
                            <button type="submit" className="btn-auth" disabled={loading}>
                                {loading ? 'Sending OTP...' : 'Send Verification OTP'}
                            </button>
                        </form>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className="auth-header">
                            <h3>Verification Code</h3>
                            <p className="text-muted">Enter the 6-digit secure code sent to <strong>{email}</strong>.</p>
                        </div>
                        <form onSubmit={handleVerifyOtp}>
                            <div className="mb-3 text-start">
                                <div className="d-flex gap-2 justify-content-center mb-3">
                                    {otp.map((data, index) => (
                                        <input
                                            className="form-control text-center fs-4"
                                            type="text"
                                            name="otp"
                                            maxLength="1"
                                            key={index}
                                            value={data}
                                            onChange={e => handleChange(e.target, index)}
                                            onKeyDown={e => handleKeyDown(e, index)}
                                            onPaste={handlePaste}
                                            onFocus={e => e.target.select()}
                                            style={{ 
                                                width: '45px', 
                                                height: '55px', 
                                                border: '2px solid #dee2e6', 
                                                borderRadius: '8px' 
                                            }}
                                            required={index === 0}
                                        />
                                    ))}
                                </div>
                            </div>
                            <button type="submit" className="btn-auth" disabled={loading}>
                                {loading ? 'Verifying...' : 'Verify Security Code'}
                            </button>
                            <div className="text-center mt-3">
                                <button 
                                    type="button" 
                                    className="btn btn-link text-decoration-none text-muted small"
                                    onClick={() => setStep(1)}
                                >
                                    Change Email
                                </button>
                            </div>
                        </form>
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className="auth-header">
                            <h3>Create New Password</h3>
                            <p className="text-muted">Enter a strong, secure password for your account.</p>
                        </div>
                        <form onSubmit={handleResetPassword}>
                            <div className="mb-3 text-start">
                                <label className="form-label">New Password</label>
                                <div className="position-relative">
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        className="form-control" 
                                        value={password} 
                                        onChange={(e) => setPassword(e.target.value)} 
                                        placeholder="Min 8 characters"
                                        required 
                                    />
                                    <button 
                                        type="button"
                                        className="btn border-0 position-absolute end-0 top-50 translate-middle-y me-2"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                                    </button>
                                </div>
                            </div>
                            <div className="mb-3 text-start">
                                <label className="form-label">Confirm New Password</label>
                                <input 
                                    type="password" 
                                    className="form-control" 
                                    value={confirmPassword} 
                                    onChange={(e) => setConfirmPassword(e.target.value)} 
                                    placeholder="Repeat new password"
                                    required 
                                />
                            </div>
                            <button type="submit" className="btn-auth" disabled={loading}>
                                {loading ? 'Updating Password...' : 'Reset Password'}
                            </button>
                        </form>
                    </>
                )}

                <div className="mt-4 text-center">
                    <small className="text-muted">
                        Remember your password? <Link to="/login" className="hover-underline text-primary fw-bold">Sign In</Link>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
